import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import type { LocalizedText, Product } from '../data/products';
import { buildWhatsAppLink } from '../utils/whatsapp';

type ProductCardProps = {
  product: Product;
  locale: 'id' | 'en';
  className?: string;
  whatsappMessage?: LocalizedText;
};

const getText = (text: LocalizedText, locale: 'id' | 'en') =>
  locale === 'en' ? text.en : text.id;

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  locale,
  className,
  whatsappMessage,
}) => {
  const navigate = useNavigate();
  const handleNavigate = () => {
    navigate(`/products/${product.slug}`);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavigate();
    }
  };

  const whatsappText = whatsappMessage ?? product.whatsappMessage;
  const whatsappLink = buildWhatsAppLink('6282151155507', getText(whatsappText, locale));

  const metaItems = [product.durationLabel, product.formatLabel].filter(Boolean) as LocalizedText[];
  const basePriceValue = product.basePriceValue;
  const promoPriceValue = product.promoPriceValue;
  const hasDiscount =
    typeof basePriceValue === 'number' &&
    typeof promoPriceValue === 'number' &&
    basePriceValue > promoPriceValue;
  const discountPercent = hasDiscount
    ? Math.round((1 - promoPriceValue / basePriceValue) * 100)
    : 0;
  const priceMain = product.priceMainLabel ?? product.priceLabel;

  return (
    <div
      className={`group cursor-pointer rounded-2xl border border-gray-200 bg-white p-6 shadow-lg transition duration-300 hover:-translate-y-0.5 hover:shadow-md ${className ?? ''}`}
      role="button"
      tabIndex={0}
      onClick={handleNavigate}
      onKeyDown={handleKeyDown}
    >
      {(product.badges && product.badges.length > 0) || hasDiscount ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {product.badges?.map((badge) => (
            <span
              key={badge}
              className="rounded-full border border-dark-blue/10 bg-dark-blue/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-dark-blue"
            >
              {badge}
            </span>
          ))}
          {hasDiscount && (
            <span className="rounded-full border border-black/10 bg-black/5 px-3 py-1 text-[11px] font-medium text-dark-blue">
              {locale === 'en' ? `SAVE ${discountPercent}%` : `Diskon ${discountPercent}%`}
            </span>
          )}
        </div>
      ) : null}
      <h3 className="text-xl font-bold text-dark-blue">{getText(product.title, locale)}</h3>
      <p className="mt-2 text-sm text-gray-600">{getText(product.tagline, locale)}</p>
      {priceMain && (
        <div className="mt-4 flex items-baseline gap-3">
          <span className="text-lg font-bold text-dark-blue">
            {getText(priceMain, locale)}
          </span>
          {product.priceStrikeLabel && (
            <span className="text-sm text-gray-400 line-through">
              {getText(product.priceStrikeLabel, locale)}
            </span>
          )}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-gray-600">
        {metaItems.map((item) => (
          <span
            key={item.id}
            className="rounded-full border border-gray-200 bg-white px-3 py-1"
          >
            {getText(item, locale)}
          </span>
        ))}
      </div>
      <div className="mt-6 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {locale === 'en' ? 'View details' : 'Lihat detail'}
        </span>
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          className="inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-50 px-4 py-2 text-xs font-semibold text-green-700 transition duration-200 hover:bg-green-100"
        >
          {locale === 'en' ? 'Chat WhatsApp' : 'Chat WhatsApp'}
          <FaWhatsapp className="text-sm" />
        </a>
      </div>
    </div>
  );
};

export default ProductCard;
